import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { FilterGroup } from 'data/filterGroup/filterGroup.types';
import * as filterGroupSelectors from 'data/filterGroup/filterGroup.selectors';
import * as actions from 'data/filterGroup/filterGroup.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import { createFilterGroupBasedOnTemplate } from 'routes/FilterGroup/templates/filterGroupTemplate.factory';
import { Button, Classes, Dialog } from '@blueprintjs/core';
import { RetrievingDataProgress } from 'components';
import FilterGroupForm, { FilterGroupFormRef } from '../../components/FilterGroupForm';
import { filterGroupCreateDialogTemplate } from '../../templates/filterGroupCreateDialogTemplate';
import getFilterGroupDataToSubmit from '../../utils/getFilterGroupDataToSubmit';
import './filterGroupCreateDialog.scss';

const mapState = (state: RootState) => ({
  isOpen: filterGroupSelectors.isFilterGroupCreatePopupOpen(state),
  isLoading: filterGroupSelectors.isFilterGroupLoading(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  filterGroup: filterGroupSelectors.getFilterGroup(state),
});

const mapDispatch = {
  createFilterGroup: actions.createFilterGroupFromForm,
  closeFilterGroupCreatePopup: actions.closeFilterGroupCreatePopup,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux;

export const FilterGroupCreateDialog: FunctionComponent<Props> = ({
  isOpen,
  isLoading,
  isAuditEnabled,
  userId,
  userName,
  filterGroup,
  createFilterGroup,
  closeFilterGroupCreatePopup,
}: Props) => {
  const [template, setTemplate] = useState(filterGroupCreateDialogTemplate);
  const [formData, setFormData] = useState<Partial<FilterGroup>>({ ...filterGroup });
  const filterGroupFormRef = useRef<FilterGroupFormRef>(null);

  useEffect(() => {
    setFormData({
      ...createFilterGroupBasedOnTemplate(filterGroupCreateDialogTemplate),
      ...filterGroup,
    });

    if (filterGroupCreateDialogTemplate != null) {
      setTemplate(filterGroupCreateDialogTemplate);
    }
  }, [isOpen, filterGroup]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
    if (template != null) {
      setTemplate(template);
    }
  };

  const handleClickCancel = () => closeFilterGroupCreatePopup();

  const handleClickSave = () => {
    const isValid = filterGroupFormRef?.current?.isValid(true, true) ?? true;

    if (!isValid) {
      return;
    }

    const formValues = filterGroupFormRef?.current?.getValues();

    if (!formValues) {
      return;
    }

    const body = getFilterGroupDataToSubmit(
      formValues,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    createFilterGroup(body);
  };
  const { t } = useTranslation();

  return (
    <div data-testid="FilterGroupCreate">
      <Dialog
        isOpen={isOpen}
        title={t('filterGroup.dialog.title')}
        onClose={handleClickCancel}
        className="filter-group-dialog"
      >
        <div className={Classes.DIALOG_BODY}>
          <FilterGroupForm
            data={formData}
            template={template}
            ref={filterGroupFormRef}
            onFieldUpdate={handleFieldUpdate}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={handleClickCancel} intent="none">{t('common.button.cancel')}</Button>
            <Button onClick={handleClickSave} intent="primary" active>{t('common.button.save')}</Button>
          </div>
        </div>
      </Dialog>
      <RetrievingDataProgress
        isOpen={isLoading}
        message={t('filterGroup.dialog.saving.message')}
      />
    </div>
  );
};

export default connector(FilterGroupCreateDialog);
